import { Link, useParams } from "wouter";
import { useBatteryLifecycle, type LifecycleEvent } from "../lib/api.js";
import { StatusPill } from "../components/StatusPill.js";

const faultLabel: Record<number, string> = {
  0: "ok",
  1: "virtual power",
  2: "Apple connector",
  3: "USB connector",
  4: "USB-C",
  5: "swelling",
  6: "auth failed",
  7: "cannot charge",
  8: "duplicate id",
  9: "recall",
  10: "health substandard",
  11: "overuse limit",
};

const eventTone: Record<string, "ok" | "warn" | "bad" | "muted"> = {
  BATTERY_IN: "ok",
  BATTERY_BORROW_OUT: "ok",
  BATTERY_ABNORMAL_WARNING: "warn",
  BATTERY_POPUP: "warn",
};

function fmtDuration(ms: number | null): string {
  if (ms === null) return "—";
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

function describe(ev: LifecycleEvent): string {
  switch (ev.event) {
    case "BATTERY_BORROW_OUT":
      return `borrowed from slot ${ev.slot ?? "?"} · ${ev.vol ?? "?"}% charge`;
    case "BATTERY_IN":
      return `returned to slot ${ev.slot ?? "?"} · ${ev.vol ?? "?"}% charge`;
    case "BATTERY_ABNORMAL_WARNING":
    case "BATTERY_POPUP": {
      const lbl = faultLabel[ev.faultType ?? 0] ?? `fault ${ev.faultType}`;
      return `${lbl}${ev.faultCause ? ` — ${ev.faultCause}` : ""}`;
    }
    default:
      return `slot ${ev.slot ?? "?"}`;
  }
}

export function BatteryLifecycle() {
  const params = useParams<{ id: string }>();
  const id = params.id;
  const { data, isLoading, error } = useBatteryLifecycle(id ?? null);

  if (!id) return null;

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <div className="mb-6 flex items-center gap-2 text-xs text-zinc-500">
        <Link href="/batteries/faulty" className="hover:text-zinc-300">Batteries</Link>
        <span>/</span>
        <span className="font-mono text-zinc-300">{id}</span>
      </div>

      {error && (
        <div className="card p-6 text-sm text-rose-300">
          Failed to load: {(error as Error).message}
        </div>
      )}

      {isLoading && <div className="card p-10 text-center text-sm text-zinc-500">Loading…</div>}

      {data && (
        <>
          <header className="card flex flex-wrap items-start justify-between gap-4 p-5">
            <div>
              <div className="font-mono text-sm text-zinc-300">{id}</div>
              <div className="mt-1 text-xs text-zinc-500">battery lifecycle</div>
            </div>
            <StatusPill tone={data.stats.currentlyOut ? "warn" : "ok"}>
              {data.stats.currentlyOut ? "currently out" : "in cabinet"}
            </StatusPill>
          </header>

          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="card p-5">
              <div className="stat-label">Total rentals</div>
              <div className="mt-2 stat-num">{data.stats.totalRentals}</div>
            </div>
            <div className="card p-5">
              <div className="stat-label">Returns</div>
              <div className="mt-2 stat-num">{data.stats.totalReturns}</div>
            </div>
            <div className="card p-5">
              <div className="stat-label">Avg rental duration</div>
              <div className="mt-2 stat-num">{fmtDuration(data.stats.avgRentalDurationMs)}</div>
            </div>
            <div className="card p-5">
              <div className="stat-label">Events captured</div>
              <div className="mt-2 stat-num">{data.stats.totalEvents}</div>
            </div>
          </div>

          <div className="mt-6 card overflow-hidden">
            <div className="flex items-center justify-between border-b border-zinc-800/60 px-4 py-3">
              <h2 className="text-sm font-semibold text-zinc-200">Timeline</h2>
              <span className="text-xs text-zinc-500">{data.events.length} events</span>
            </div>
            {data.events.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-zinc-500">
                No events yet for this battery.
              </div>
            ) : (
              <ol className="divide-y divide-zinc-800/60">
                {data.events.map((ev) => (
                  <li key={ev.id} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-3">
                    <StatusPill tone={eventTone[ev.event] ?? "muted"}>{ev.event}</StatusPill>
                    <span className="truncate text-sm text-zinc-300">{describe(ev)}</span>
                    <span className="font-mono text-[11px] text-zinc-500">
                      <Link href={`/cabinets/${ev.cabinetId}`} className="hover:text-zinc-300">
                        {ev.cabinetId}
                      </Link>
                      {" · "}
                      {new Date(ev.timestamp).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      )}
    </div>
  );
}
